import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import { TrendingUp as TrendingUpIcon } from "@mui/icons-material";

import { formatCurrency, formatHandlerCode } from "../../../utils/namesFormater";
import { formatProductType } from "../../../utils/enumFormatter";
import { colorAdjustedProductType } from "../../../utils/colorEnumFormatter";
import MimshakDataTable from "./MimshakDataTable";
import { MimshakData } from "../../../interfaces/DTO/mimshakData";
import { LOCAL_STORAGE_KEYS } from "../../../services/local_storage_keys";
import HierarchyToggle from "../../../shared/HierarchyToggle";
import { AdjustedProductType, ProductType } from "../../../interfaces/DTO/mislaka_xml_models";
import { useMimshakScatterDrawer } from "../../../shared/UIParts/MimshakScatterDrawer";
import AddCommentButton from "../../CRM/contacts/AddCommentButton";
import CreateRecurringDepositChangeButton from "../../UserTask/Workflow/CreateRecurringDepositChangeButton";
import CreateOneTimeDepositButton from "../../UserTask/Workflow/CreateOneTimeDepositButton";
import { Contact } from "../../../interfaces/DTO/crm/contact";


interface AllMimshakDataTablesProps {
  data: MimshakData[];
  viewedType: "USER" | "FAMILY";
  selectedPeriodType: "REGULAR" | "PRE_ADVICE";
  contacts?: Contact[];
  reportedPeriod?: string;
}

const BLOCKED_DRAWER_TYPES: ProductType[] = [
  ProductType.PURE_RISK_POLICY,
  ProductType.COLLECTIVE_RISK_POLICY,
  ProductType.MORTGAGE_LIFE_INSURANCE,
];

const isDrawerAllowedForType = (productType: ProductType): boolean =>
  !BLOCKED_DRAWER_TYPES.includes(productType);

const AllMimshakDataTables: React.FC<AllMimshakDataTablesProps> = ({
  data,
  viewedType,
  selectedPeriodType,
  contacts = [],
  reportedPeriod,
}) => {
  const [hierarchy, setHierarchy] = useState<"handlerFirst" | "productFirst">("handlerFirst");
  const [selectedByGroup, setSelectedByGroup] = useState<Record<string, MimshakData[]>>({});
  const { open: openDrawer, drawer } = useMimshakScatterDrawer();

  const grouped = useMemo(() => {
    if (hierarchy === 'handlerFirst') {
      const groupedByHandler: Record<string, MimshakData[]> = {};
      data.forEach((item) => {
        const handler = formatHandlerCode(item.handler_code);
        if (!groupedByHandler[handler]) groupedByHandler[handler] = [];
        groupedByHandler[handler].push(item);
      });
      return groupedByHandler;
    } else {
      const groupedByType: Record<string, MimshakData[]> = {};
      data.forEach((item) => {
        let typeKey = item.product_type;

        if (
          item.product_type === ProductType.PURE_SAVINGS_POLICY &&
          [AdjustedProductType.ACTIVE_MANAGERS_INSURANCE, AdjustedProductType.INACTIVE_MANAGERS_INSURANCE].includes(item.adjusted_product_type!)
        ) {
          typeKey = ProductType.LIFE_INSURANCE_SAVINGS;
        }

        const type = formatProductType(typeKey);
        if (!groupedByType[type]) groupedByType[type] = [];
        groupedByType[type].push(item);
      });
      return groupedByType;
    }
  }, [data, hierarchy]);

  return (
    <Box dir="rtl">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">
          טבלאות דיווחים לפי {hierarchy === 'handlerFirst' ? 'יצרן' : 'סוג מוצר'}
        </Typography>
        <HierarchyToggle
          onChange={setHierarchy}
          storageKey={LOCAL_STORAGE_KEYS.mimshakHierarchyView_Main_tab}
        />
      </Box>

      {Object.entries(grouped).map(([groupName, groupData]) => {
        const total = groupData.reduce((sum, item) => sum + (item.funds_sum || 0), 0);

        const groupSelection = selectedByGroup[groupName] ?? [];
        const firstSelected = groupSelection[0];

        const commentProductType = firstSelected
          ? firstSelected.product_type
          : hierarchy === 'productFirst' ? groupData[0]?.product_type : undefined;
        const commentHandlerCode = firstSelected
          ? firstSelected.handler_code
          : hierarchy === 'handlerFirst' ? groupData[0]?.handler_code : undefined;
        const commentPolicyNumber = firstSelected?.policy_number ?? undefined;

        return (
          <Box key={groupName} mb={4}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="h5">
                {groupName} | {formatCurrency(total)}
              </Typography>

              {hierarchy === 'productFirst' &&
                groupData.length > 0 &&
                isDrawerAllowedForType(groupData[0].product_type) && (
                  <IconButton
                    onClick={() => openDrawer(groupData, groupName)}
                    sx={{
                      backgroundColor: colorAdjustedProductType(groupData[0].adjusted_product_type ?? groupData[0].product_type),
                      color: 'white',
                      '&:hover': {
                        backgroundColor: colorAdjustedProductType(groupData[0].adjusted_product_type ?? groupData[0].product_type),
                        opacity: 0.8,
                      },
                    }}
                    title="הצג תרשימי פיזור תשואות"
                  >
                    <TrendingUpIcon />
                  </IconButton>
                )}
            </Box>

            <MimshakDataTable
              data={groupData}
              viewedType={viewedType}
              selectedPeriodType={selectedPeriodType}
              headerColor={
                hierarchy === 'productFirst'
                  ? colorAdjustedProductType(groupData[0].adjusted_product_type ?? groupData[0].product_type)
                  : undefined
              }
              onSelectionChange={(selected) =>
                setSelectedByGroup((prev) => ({ ...prev, [groupName]: selected }))
              }
            />

            {(contacts.length > 0 || groupSelection.length === 1) && (
              <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", mt: -1, px: 2, mb:-1 }}>
                {contacts.length > 0 && (
                  <AddCommentButton
                    contacts={contacts}
                    isFamily={viewedType === "FAMILY"}
                    reportedPeriod={reportedPeriod}
                    productType={commentProductType}
                    handlerCode={commentHandlerCode}
                    policyNumber={commentPolicyNumber}
                  />
                )}
                {groupSelection.length === 1 && (
                  <>
                    <CreateRecurringDepositChangeButton row={firstSelected} />
                    <CreateOneTimeDepositButton row={firstSelected} />
                  </>
                )}
              </Box>
            )}
          </Box>
        );
      })}

      {drawer}
    </Box>
  );
};

export default AllMimshakDataTables;
